import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usageService } from '../utils/api';
import { TrendingUp, Activity, CheckCircle, XCircle, Crown, ArrowUpRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import api from '../utils/api';

const DashboardOverview = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Small delay to ensure auth is ready
    const timer = setTimeout(() => {
      if (mountedRef.current) {
        loadOverview();
        loadSubscription();
      }
    }, 200);
    
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
    };
  }, []);

  const loadOverview = async (retryCount = 0) => {
    try {
      const response = await usageService.getOverview();
      if (mountedRef.current) {
        setOverview(response.data.data);
      }
    } catch (error) {
      // Retry up to 2 times
      if (retryCount < 2 && mountedRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return loadOverview(retryCount + 1);
      }
      if (mountedRef.current) {
        toast.error('Failed to load dashboard data');
      }
      console.error(error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const loadSubscription = async (retryCount = 0) => {
    try {
      const response = await api.get('/api/payment/subscription');
      if (mountedRef.current) {
        setSubscription(response.data);
      }
    } catch (error) {
      // Retry up to 2 times
      if (retryCount < 2 && mountedRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return loadSubscription(retryCount + 1);
      }
      console.error('Failed to load subscription:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const stats = overview?.stats || {};
  const recentLogs = overview?.recentLogs || [];

  const getPlanBadge = (plan) => {
    const badges = {
      free: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200', label: 'Free' },
      starter: { color: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200', label: 'Starter' },
      professional: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200', label: 'Professional' },
      enterprise: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200', label: 'Enterprise' }
    };
    return badges[plan] || badges.free;
  };

  const getUsagePercentage = () => {
    if (!subscription?.limits) return 0;
    return Math.round((subscription.limits.requestsUsed / subscription.limits.monthlyRequests) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Page Header with Subscription Status */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Dashboard Overview</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Monitor your API usage and performance metrics
          </p>
        </div>
        {subscription && (
          <div className={`px-4 py-2 rounded-full text-sm font-semibold ${getPlanBadge(subscription.plan).color}`}>
            {getPlanBadge(subscription.plan).label}
            {subscription.status === 'active' && subscription.plan !== 'free' && (
              <span className="ml-2 inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            )}
          </div>
        )}
      </div>

      {/* Current Plan Card */}
      {subscription && (
        <div className="card bg-gradient-to-r from-primary-50 to-orange-50 dark:from-primary-900/20 dark:to-orange-900/20 border-primary-200 dark:border-primary-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white dark:bg-dark-900 rounded-lg shadow-sm">
                <Crown className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="text-xl font-bold">{getPlanBadge(subscription.plan).label} Plan</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPlanBadge(subscription.plan).color}`}>
                    {subscription.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {subscription.limits.requestsUsed.toLocaleString()} / {subscription.limits.monthlyRequests.toLocaleString()} requests used ({getUsagePercentage()}%)
                </p>
                <div className="w-64 h-2 bg-gray-200 dark:bg-dark-800 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary-600 to-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(getUsagePercentage(), 100)}%` }}
                  />
                </div>
              </div>
            </div>
            {subscription.plan === 'free' && (
              <Link to="/pricing" className="btn-primary flex items-center space-x-2">
                <span>Upgrade Plan</span>
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.apiKeysCount || 0}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Active API Keys</div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.todayRequests || 0}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Requests Today</div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.monthRequests || 0}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">This Month</div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Activity className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.totalRequests || 0}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Requests</div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        
        {recentLogs.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No activity yet. Start using your API keys!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-800">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    Time
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    Endpoint
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    Response Time
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    API Key
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.map((log, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-dark-800">
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-sm bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">
                        {log.method} {log.endpoint}
                      </code>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`badge ${
                        log.statusCode < 400 ? 'badge-success' : 'badge-error'
                      }`}>
                        {log.statusCode}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {log.responseTime}ms
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {log.apiKeyId?.keyPrefix || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card bg-gradient-to-br from-primary-50 to-pink-50 dark:from-primary-900/20 dark:to-pink-900/20 border-primary-200 dark:border-primary-800">
          <h3 className="text-lg font-semibold mb-2">Need API Keys?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create and manage your API keys to start using the service
          </p>
          <a href="/dashboard/keys" className="btn-primary inline-block">
            Manage API Keys
          </a>
        </div>

        <div className="card bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-semibold mb-2">Test the API</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Try out the API in our interactive playground
          </p>
          <a href="/dashboard/playground" className="btn-primary inline-block">
            Open Playground
          </a>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
